/** O vocabulário de faceta da FOTO congelada, do lado do navegador.
 *
 * Espelho de `web_api/infrastructure/persistence/snapshot_facets.py`: as
 * mesmas dimensões, os mesmos nomes de parâmetro, a mesma regra de
 * "valor desconhecido não é erro". Mora em `shared/` porque desde
 * 2026-09-08 são DUAS telas lendo a mesma foto com o mesmo recorte — o
 * dashboard, que agrega, e a lista de menções do relatório, que pagina.
 *
 * Estava tudo local do `GlobalDashboardPage`. Deixar lá e copiar pra
 * outra tela é o que faz as duas divergirem no primeiro filtro novo, e o
 * sintoma seria um total que não bate com o da tela vizinha — a mesma
 * coisa que a paridade do lado do servidor existe pra impedir.
 *
 * O que NÃO veio junto, de propósito: a paleta de sentimento e o
 * `percentChange` são de GRÁFICO, não de recorte, e ficaram lá.
 */

import {
  DASHBOARD_NULL_KEY,
  type DashboardMetric,
  type SocialReportDashboardFilters
} from "@/shared/api/api-client";

/**
 * As onze dimensões de faceta do dashboard, declaradas UMA vez.
 *
 * Cada uma aparecia em oito lugares deste arquivo: dois `useState`, a
 * leitura da URL, o corpo do `load`, a serialização de volta pra URL, o
 * `resetFilters`, o contador do badge, as `facetOptions` e as duas
 * listas de dependência. Acrescentar a décima segunda exigia lembrar
 * dos oito, e esquecer um não quebrava nada visível — só fazia o filtro
 * sumir do link, ou o badge não acender.
 *
 * `catalog` é o nome do campo em `filterCatalog` (o que o relatório
 * devolveu); `null` ali significa "esta foto não tem a dimensão", que é
 * diferente de "ainda não carregou".
 *
 * O JSX do painel NÃO é gerado a partir daqui de propósito: cada filtro
 * carrega rótulo, dica de meio-escopo e, no caso do porta-voz, a
 * mensagem de relatório antigo — conteúdo que só faz sentido escrito por
 * extenso, e que um laço genérico transformaria em tabela de strings.
 */
export const DASHBOARD_DIMENSIONS = [
  { key: "sentiments", catalog: null },
  { key: "sources", catalog: null },
  { key: "channels", catalog: "channels" },
  { key: "approaches", catalog: "approaches" },
  { key: "formats", catalog: "formats" },
  { key: "tiers", catalog: "tiers" },
  // Tipo de mídia congelado (2026-11-01): vocabulário FECHADO, sem
  // catálogo da foto — as opções vêm de `SNAPSHOT_CLOSED_CHOICES`.
  { key: "mediaTypes", catalog: null },
  { key: "influencerTypes", catalog: "influencerTypes" },
  { key: "publisherGroups", catalog: "publisherGroups" },
  { key: "themeGroups", catalog: "themeGroups" },
  { key: "themes", catalog: "themes" },
  { key: "spokespersons", catalog: "spokespersons" },
  // Marca entrou em 2026-09-08, quando a lista de menções da foto ganhou
  // filtro de leitura — e o dashboard herdou junto, porque o breakdown
  // dele já existia. É a única dimensão cujo VALOR é o id em texto: na
  // foto ela é coluna própria, não nome congelado. É o mesmo `key` que o
  // breakdown devolve, então o que a tela manda é o que ela recebeu.
  { key: "brands", catalog: "brands" }
] as const;

export type DashboardDimension = (typeof DASHBOARD_DIMENSIONS)[number]["key"];

/** As dimensões da foto com vocabulário FECHADO: as opções não dependem
 *  do que o relatório devolveu. O tipo de mídia é a chave congelada
 *  (`core/vocabulary.py::MEDIA_TYPES`), e `DASHBOARD_NULL_KEY` é a
 *  imprensa sem tipo — inclusive toda a de foto anterior à coluna. */
export const SNAPSHOT_CLOSED_CHOICES: Partial<
  Record<DashboardDimension, Array<{ value: string; label: string }>>
> = {
  mediaTypes: [
    { value: "print", label: "Impresso" },
    { value: "online", label: "Online" },
    { value: "tv", label: "TV" },
    { value: "radio", label: "Rádio" },
    { value: DASHBOARD_NULL_KEY, label: "Não informado" },
  ],
};

/** O par que toda dimensão tem: o que foi escolhido, e se é "exceto". */
export type DashboardFacet = { values: Set<string>; exclude: boolean };

export type DashboardFacets = Record<DashboardDimension, DashboardFacet>;

/** O recorte no formato que o cliente HTTP espera.
 *
 *  Tipado como `SocialReportDashboardFilters` e não `Record<string,
 *  unknown>`: é o que mantém o compilador conferindo os nomes das onze
 *  dimensões contra o contrato do cliente, que é justamente a checagem
 *  que se perderia ao trocar vinte e duas variáveis por um laço. */
export function facetPayload(facets: DashboardFacets): SocialReportDashboardFilters {
  const payload: SocialReportDashboardFilters = {};
  DASHBOARD_DIMENSIONS.forEach(({ key }) => {
    payload[key] = Array.from(facets[key].values);
    payload[excludeParam(key)] = facets[key].exclude;
  });
  return payload;
}

/** O nome do parâmetro de exclusão: `themes` → `excludeThemes`.
 *
 *  O tipo de retorno é literal e não `string` de propósito — é ele que
 *  deixa o resultado indexar `SocialReportDashboardFilters`, e portanto
 *  o compilador conferir que os onze pares existem no contrato do
 *  cliente. É a mesma construção que o `api-client` já faz do lado de
 *  lá, ao montar a query. */
export type ExcludeParam = `exclude${Capitalize<DashboardDimension>}`;

export function excludeParam(key: DashboardDimension): ExcludeParam {
  return `exclude${(key[0].toUpperCase() + key.slice(1)) as Capitalize<DashboardDimension>}`;
}

/** O prefixo do filtro de LEITURA na querystring.
 *
 * Existe porque a tela de relatório carrega DOIS recortes ao mesmo
 * tempo: o de geração, que vira foto congelada, e o de leitura, que é
 * volátil. Sem prefixo eles colidem em `from`, `to` e `minAdValue` — e a
 * colisão não é estética: um `minAdValue` escolhido só pra LER seria
 * lido de volta como recorte de geração no F5 e congelaria na próxima
 * foto. O dashboard não precisa dele (lá só existe um recorte), então o
 * prefixo é parâmetro e não default. */
export const VIEW_PREFIX = "view.";

export function readFacets(
  params: URLSearchParams,
  prefix = ""
): DashboardFacets {
  return Object.fromEntries(
    DASHBOARD_DIMENSIONS.map(dimension => [
      dimension.key,
      {
        values: new Set(params.getAll(prefix + dimension.key)),
        exclude: params.get(prefix + excludeParam(dimension.key)) === "1"
      }
    ])
  ) as DashboardFacets;
}

/** Escreve o recorte na query, no formato que o `readFacets` lê de
 *  volta. A flag só viaja quando é `true`, mesma economia do
 *  `recorteToSearch`: `exclude=0` é o padrão e não diz nada. */
export function writeFacets(
  query: URLSearchParams,
  facets: DashboardFacets,
  prefix = ""
): void {
  for (const { key } of DASHBOARD_DIMENSIONS) {
    facets[key].values.forEach(value => query.append(prefix + key, value));
    if (facets[key].exclude) query.set(prefix + excludeParam(key), "1");
  }
}

export function emptyFacets(): DashboardFacets {
  return readFacets(new URLSearchParams());
}

/** Quantas dimensões estão recortando. Conta DIMENSÕES e não valores:
 *  é o número que o gatilho do painel mostra, e "3" querendo dizer três
 *  filtros é mais legível que "3" querendo dizer três temas. */
export function activeFacetCount(facets: DashboardFacets): number {
  return DASHBOARD_DIMENSIONS.filter(({ key }) => facets[key].values.size > 0)
    .length;
}

/** Vocabulário fechado do servidor (a coluna congelada `post_source`). */
export const sourceOptions = [
  { value: "network", label: "Redes sociais" },
  { value: "press", label: "Imprensa" }
];

export function humanizeLabel(value: string): string {
  const normalized = value.trim().toLocaleLowerCase("pt-BR");
  const known: Record<string, string> = {
    favoravel: "Favorável", desfavoravel: "Desfavorável", positivo: "Positivo",
    negativo: "Negativo", neutro: "Neutro", sem_regra: "Não classificado",
    rede_social: "Redes sociais", redes_sociais: "Redes sociais", noticia: "Notícias",
    imprensa: "Imprensa", network: "Redes sociais", press: "Imprensa",
    // Abordagem. Entrou em 2026-08-29, quando a foto passou a guardar o
    // vocabulário interno: sem estas três, o fallback title-case
    // rotularia as fatias como "Protagonist" e "Supporting", em inglês
    // e sem acento. Antes o fallback acertava por acidente, porque o
    // valor gravado era o PT-BR maiúsculo.
    protagonist: "Protagonista", supporting: "Coadjuvante", citation: "Citação",
    // Tipo de mídia congelado (2026-11-01).
    print: "Impresso", online: "Online", tv: "TV", radio: "Rádio",
    [DASHBOARD_NULL_KEY]: "Não informado"
  };
  if (known[normalized]) return known[normalized];
  return value.replaceAll("_", " ").trim().split(/\s+/).map(word => {
    const [first = "", ...rest] = Array.from(word);
    return `${first.toLocaleUpperCase("pt-BR")}${rest.join("")}`;
  }).join(" ");
}

/** Quando o servidor tem um rótulo PRÓPRIO (nome da marca, "Não
 * informado"), ele manda e a gente respeita. Quando `label === key`, é
 * só o valor cru da coluna congelada (`network`, `POSITIVO`) — aí a
 * apresentação é nossa. */
export const metricLabel = (item: DashboardMetric) =>
  item.label && item.label !== item.key ? item.label : humanizeLabel(item.key);

/** Opções de faceta: o que a foto devolveu (com o rótulo pronto do
 * servidor, inclusive "Não informado") mais o que já está selecionado,
 * pra um valor vindo do link não sumir da lista.
 *
 * Sempre PLANO, sem título de grupo: a foto guarda tema e grupo de tema
 * como listas de nomes congelados sem elo entre si, então não há de onde
 * tirar o grupo de cada tema (só o catálogo vivo, que pode ter mudado).
 *
 * `metrics === null` = dimensão que esta foto não tem. Sem o branch o
 * `.map` estoura `TypeError`. `choices` é o vocabulário fechado
 * (`SNAPSHOT_CLOSED_CHOICES`), que vale sem catálogo da foto. */
export function facetOptions(
  selected: Set<string>,
  metrics: DashboardMetric[] | null,
  choices?: Array<{ value: string; label: string }>
): Array<{ value: string; label: string }> {
  const byKey = new Map(
    choices
      ? choices.map(choice => [choice.value, choice.label] as const)
      : (metrics ?? []).map(item => [item.key, metricLabel(item)] as const)
  );
  selected.forEach(value => {
    if (!byKey.has(value)) byKey.set(value, humanizeLabel(value));
  });
  return Array.from(byKey, ([value, label]) => ({ value, label }));
}
